/** Contacts tab - table of supplier contacts by type, with local add/edit/remove (Demo
 * Mode only, local-only - no backend persistence). See `supplierProfileData.ts`. */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Delete, Edit, Add } from '@mui/icons-material';
import { Section } from '../../../../../components/ui/Section';
import {
  getSupplierContacts, SupplierContact, SupplierContactType,
} from '../../../../../mocks/data/supplierProfileData';

export interface ContactsTabProps {
  supplierId: number;
}

const CONTACT_TYPES: SupplierContactType[] = ['AccountManager', 'CustomerService', 'Ordering', 'Finance', 'Escalation', 'OutOfHours'];

const emptyContact = (): SupplierContact => ({
  id: 0, type: 'AccountManager', name: '', role: '', email: '', phone: '', isPrimary: false,
});

const ContactsTab: React.FC<ContactsTabProps> = ({ supplierId }) => {
  const { t } = useTranslation('suppliers');
  const [contacts, setContacts] = useState<SupplierContact[]>(() => getSupplierContacts(supplierId));
  const [editing, setEditing] = useState<SupplierContact | null>(null);

  useEffect(() => {
    setContacts(getSupplierContacts(supplierId));
  }, [supplierId]);

  const handleSave = () => {
    if (!editing) return;
    setContacts((current) => {
      if (editing.id === 0) {
        return [...current, { ...editing, id: Date.now() }];
      }
      return current.map((contact) => (contact.id === editing.id ? editing : contact));
    });
    setEditing(null);
  };

  const handleRemove = (id: number) => {
    setContacts((current) => current.filter((contact) => contact.id !== id));
  };

  return (
    <Section>
      <Box sx={{ p: 4 }}>
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}>
          <Typography variant="textSM"
            fontWeight={600}>{t('supplierContacts', 'Supplier contacts')}</Typography>
          <Button variant="secondary"
            size="md"
            startIcon={<Add />}
            onClick={() => setEditing(emptyContact())}>
            {t('addContact', 'Add contact')}
          </Button>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('contactType', 'Contact type')}</TableCell>
              <TableCell>{t('name', 'Name')}</TableCell>
              <TableCell>{t('role', 'Role')}</TableCell>
              <TableCell>{t('emailAddress', 'Email')}</TableCell>
              <TableCell>{t('phone', 'Phone')}</TableCell>
              <TableCell>{t('primaryContact', 'Primary')}</TableCell>
              <TableCell>{t('notes', 'Notes')}</TableCell>
              <TableCell align="right">{t('actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.length === 0 && (
              <TableRow><TableCell colSpan={8}>{t('noContacts', 'No contacts captured yet.')}</TableCell></TableRow>
            )}
            {contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell>{contact.type}</TableCell>
                <TableCell>{contact.name}</TableCell>
                <TableCell>{contact.role}</TableCell>
                <TableCell>{contact.email}</TableCell>
                <TableCell>{contact.phone}</TableCell>
                <TableCell>{contact.isPrimary && <Chip label={t('primary', 'Primary')}
                  size="small"
                  color="success" />}</TableCell>
                <TableCell>{contact.notes ?? '-'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small"
                    onClick={() => setEditing(contact)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small"
                    onClick={() => handleRemove(contact.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={!!editing}
        onClose={() => setEditing(null)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{editing?.id === 0 ? t('addContact', 'Add contact') : t('editContact', 'Edit contact')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3}
            sx={{ mt: 1 }}>
            <TextField select
              label={t('contactType', 'Contact type')}
              value={editing?.type ?? 'AccountManager'}
              onChange={(e) => setEditing((current) => (current ? { ...current, type: e.target.value as SupplierContactType } : current))}
            >
              {CONTACT_TYPES.map((type) => <MenuItem key={type}
                value={type}>{type}</MenuItem>)}
            </TextField>
            <TextField label={t('name', 'Name')}
              value={editing?.name ?? ''}
              onChange={(e) => setEditing((current) => (current ? { ...current, name: e.target.value } : current))} />
            <TextField label={t('role', 'Role')}
              value={editing?.role ?? ''}
              onChange={(e) => setEditing((current) => (current ? { ...current, role: e.target.value } : current))} />
            <TextField label={t('emailAddress', 'Email')}
              value={editing?.email ?? ''}
              onChange={(e) => setEditing((current) => (current ? { ...current, email: e.target.value } : current))} />
            <TextField label={t('phone', 'Phone')}
              value={editing?.phone ?? ''}
              onChange={(e) => setEditing((current) => (current ? { ...current, phone: e.target.value } : current))} />
            <TextField label={t('notes', 'Notes')}
              value={editing?.notes ?? ''}
              onChange={(e) => setEditing((current) => (current ? { ...current, notes: e.target.value } : current))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setEditing(null)}>{t('cancel', 'Cancel')}</Button>
          <Button variant="primary"
            onClick={handleSave}>{t('save', 'Save')}</Button>
        </DialogActions>
      </Dialog>
    </Section>
  );
};

export default ContactsTab;
